<template class="customer-service-ratings">
  <div>
    <ColumnChart
      :request-status="requestStatus"
      :categories="formattedCategories(categories)"
      :series="series"
      :title="title"
      :item-id="itemId"
      :card-options="cardOptions"
      :open-filter="openFilter"
      :has-custom-filter="true"
      :show-granularity="false"
      v-bind="$attrs"
      @reload="getData"
    />
    <v-dialog
      v-model="filterDialog"
      width="50%"
      overlay-color="var(--ds-color-reverse)"
    >
      <v-card>
        <v-toolbar>
          <v-toolbar-title>
            {{ filterTitleLabel }}
          </v-toolbar-title>
          <v-spacer />
          <v-toolbar-items class="customer-service-ratings__close-icon">
            <span>
              <StIcon
                class="close-icon"
                icon-name="close"
                @click="filterDialog = false"
              />
            </span>
          </v-toolbar-items>
        </v-toolbar>

        <v-row
          no-gutters
          class="mt-5 mx-5"
        >
          <MultiSelectFilter
            v-model="selectedOperators"
            class="mr-3"
            :select-items="getRepliers"
            item-value="id"
            :label="$t('components.OperatorsLogs.replier')"
            :searchable="true"
          />
        </v-row>

        <v-row
          no-gutters
          class="pb-5 px-5"
        >
          <v-spacer />
          <v-spacer />
          <v-btn
            v-if="hasFilter"
            outlined
            rounded
            class="mr-3"
            @click="clearFilter"
          >
            {{ $t('components.OperatorsLogs.trashLabel') }}
          </v-btn>
          <v-btn
            outlined
            rounded
            @click="applyFilter"
          >
            {{ $t('components.OperatorsLogs.filterLabel') }}
          </v-btn>
        </v-row>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ColumnChart from '@/components/cards/models/ColumnChart';
import channelModel from '@/mixins/channel-model';
import cardsModel from '@/mixins/cards-model';
import { mapActions } from 'vuex';
import EventBus from '@/utils/event-bus';
import { SAC_SURVEYS_FIELDS, OPERATION, POINTS_PARSER, ENTITIES } from '@/services/charts-service';
import WarRoomService from '@/services/WarRoomService';
import repliersModel from '@/mixins/repliers-model';
import MultiSelectFilter from '@/components/filter/MultiSelectFilter.vue';
import ratingModel from '@/mixins/rating-model';

export default {
  name: 'CSATBarChart',
  components: {
    ColumnChart,
    MultiSelectFilter,
  },
  mixins: [
    channelModel,
    cardsModel,
    repliersModel,
    ratingModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    ownerPosts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: {},
      classifiedPercentage: 0,
      selectedOperators: [],
      filterDialog: false,
      hasFilter: false,
      componentFilter: null,
    };
  },
  computed: {
    categories() {
      return [...new Set(this.rawSeries?.points?.map(({ coordinates }) => coordinates.surveyDate))];
    },
    getRepliers() {
      return this.repliersToSelectItem(this.activeDashboardFilters?.repliers);
    },
    filterTitleLabel() {
      return `Filtro - ${this.title}`;
    },
    series() {
      const seriesObj = [];

      if (!this.categories?.length) {
        return;
      }

      this.rawSeries?.points?.map(({ coordinates, value }) => {
        const score = coordinates.surveyScore;

        if (!score) {
          return;
        }

        const { name, color } = this.getRatingObj(score);
        const serie = seriesObj.find((serie) => serie.name === name);
        const data = serie?.data || [];

        data[this.categories?.indexOf(coordinates.surveyDate)] = value;

        if (serie) {
          return;
        }

        seriesObj.push({
          name,
          color,
          data,
        });
      });

      seriesObj.forEach((serie) => {
        for (let i = 0; i < serie.data.length; i++) {
          serie.data[i] = [this.categories[i], serie.data[i] || null];
        }
      });
      return seriesObj;
    },
    cardOptions() {
      return {
        xAxis: {
          type: 'datetime',
          labels: {
            format: this.labelFormat,
          },
        },
        legend: {
          enabled: true,
        },
        tooltip: {
          xDateFormat: this.xDateFormat,
        },
      };
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
      'getWarroomService',
    ]),
    async getFilterId() {
      const sacRepliers = this.selectedOperators ? this.selectedOperators.value : null;
      const newCardFilter = {
        sac_replier: {
          value: sacRepliers,
        },
        is_sac: true,
      };
      return WarRoomService.encodeFilter({
        account_id: this.accountId,
        is_staging: this.isStaging,
        filter: JSON.stringify(newCardFilter),
      });
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      this.applyFilter(options);
    },
    async applyFilter(options) {
      this.componentFilter = await this.getFilterId();
      this.getChartsService({
        itemId: this.itemId,
        entity: ENTITIES.SAC_SURVEYS,
        method: 'getTimeSeries',
        points_parser: POINTS_PARSER.COORDINATES,
        params: {
          customFilterId: this.componentFilter,
          grouping: [SAC_SURVEYS_FIELDS.surveyScore, SAC_SURVEYS_FIELDS.surveyDate],
          operators: this.operation,
          sortFields: [SAC_SURVEYS_FIELDS.surveyDate],
          datePortion: this.getDatePortion(options),
          label: SAC_SURVEYS_FIELDS.surveyScore,
        },
      })
        .then(response => {
          response.points.sort((a, b) => {
            const aScore = a.coordinates.surveyScore;
            const bScore = b.coordinates.surveyScore;
            return aScore > bScore ? -1 : 0;
          });
          this.rawSeries = response;
          this.requestStatus = 'success';
          this.filterDialog = false;
          this.hasFilter = true;

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch(error => {
          console.error(error);
          this.requestStatus = 'error';
        });
    },
    formattedCategories(categories) {
      return this.isQuarterOnDatePortion
        ? categories.map(x => this.formattedLabelForQuarter(x))
        : categories;
    },
    openFilter() {
      this.filterDialog = true;
    },
    clearFilter() {
      this.selectedOperators = [];
      this.hasFilter = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.customer-service-ratings {
  &__close-icon {
    align-items: center;
  }
}

::v-deep {
  .customer-service-ratings {
    &__table-truncate {
      td {
        max-width: 10vw;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    &__modal-style {
      * {
        color: var(--ds-color-snow) !important;
      }

      h3 {
        font-weight: var(--ds-font-weight-bold);
      }
    }

    &__v-dialog {
      overflow-x: hidden;
    }
  }
}
</style>
